import React, { useCallback, useMemo, useRef } from 'react';
import Popover from 'react-popover';
import { css } from 'emotion';
import PropTypes from 'prop-types';
import { useMaxZ } from '../../hooks/services/use-max-z';
import { noop } from '../../default-props/noop';
import { useClickOutside } from '../../hooks/use-click-outside';
import { GlobalEventListener } from '../global-event-listener';
import { emptyObject } from 'ui-components/default-props/empty';
import { useEnableNewHotkeySystem } from '../../../feature-flags/hooks/use-enable-new-hotkey-system';

/*
  DO NOT USE

  Popover as a UI concept is not necessarily bad
  The underlying react-popover library is 4 years unmaintained and introduces extra dimensions of issues when debugging

  FL - SPD-10778 tracks our effort to consolidate components
*/

const BODY = document.querySelector('body');

const PopoverComponent = React.forwardRef(
  (
    {
      appendTarget,
      body,
      children,
      isOpen,
      onOuterAction,
      popoverBodyStyles,
      popoverIsOpen,
      popoverStyles,
      popoverTipShapeStyles,
      popoverTipStyles,
      backgroundColor = '#fff',
      ...props
    },
    ref,
  ) => {
    const isNewHotkeySystemEnabled = useEnableNewHotkeySystem();
    const zIndex = useMaxZ();
    const popOverStyles = css`
      z-index: ${zIndex};
      ${popoverStyles}
      .Popover-body {
        text-align: left;
        background-color: ${backgroundColor};
        background-clip: padding-box;
        border-radius: 6px;
        box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
        white-space: normal;
        ${popoverBodyStyles}
      }
      .Popover-tip {
        ${popoverTipStyles}
        .Popover-tipShape {
          fill: ${backgroundColor};
          ${popoverTipShapeStyles}
        }
      }
    `;

    const bodyRef = useRef(null);
    const wrappedBody = useMemo(() => <div ref={bodyRef}>{body}</div>, [body]);

    const wrappedOnClick = useCallback(
      (e) => {
        if (bodyRef?.current && !bodyRef.current.contains(e.target)) {
          onOuterAction();
        }
      },
      [bodyRef, onOuterAction],
    );

    useClickOutside(bodyRef, onOuterAction, { enabled: isNewHotkeySystemEnabled && isOpen });

    const popoverElement = (
      <Popover
        ref={ref}
        appendTarget={appendTarget}
        className={popOverStyles}
        enterExitTransitionDurationMs={0}
        isOpen={isOpen}
        place={null}
        popoverIsOpen={popoverIsOpen}
        preferPlace='left'
        {...props}
        body={wrappedBody}
      >
        {children}
      </Popover>
    );

    if (isNewHotkeySystemEnabled) {
      return popoverElement;
    }

    // Old system: GEL onClick subscription
    return <GlobalEventListener onClick={wrappedOnClick}>{popoverElement}</GlobalEventListener>;
  },
);

PopoverComponent.displayname = 'PopoverComponent';
PopoverComponent.propTypes = {
  appendTarget: PropTypes.object.isRequired,
  body: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOuterAction: PropTypes.func.isRequired,
  popoverBodyStyles: PropTypes.string.isRequired,
  popoverIsOpen: PropTypes.bool.isRequired,
  popoverStyles: PropTypes.string.isRequired,
  popoverTipShapeStyles: PropTypes.string.isRequired,
  popoverTipStyles: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
};

export const PopoverWrapper = React.forwardRef(
  (
    {
      appendTarget = BODY,
      body = emptyObject,
      children,
      isOpen = false,
      onOuterAction = noop,
      popoverBodyStyles = '',
      popoverIsOpen = false,
      popoverStyles = '',
      popoverTipShapeStyles = '',
      popoverTipStyles = '',
      ...props
    },
    ref,
  ) => {
    if (!isOpen) {
      return <React.Fragment>{children}</React.Fragment>;
    }

    return (
      <PopoverComponent
        ref={ref}
        appendTarget={appendTarget}
        body={body}
        isOpen={isOpen}
        onOuterAction={onOuterAction}
        popoverBodyStyles={popoverBodyStyles}
        popoverIsOpen={popoverIsOpen}
        popoverStyles={popoverStyles}
        popoverTipShapeStyles={popoverTipShapeStyles}
        popoverTipStyles={popoverTipStyles}
        {...props}
      >
        {children}
      </PopoverComponent>
    );
  },
);

PopoverWrapper.displayName = 'PopoverWrapper';
PopoverWrapper.propTypes = {
  appendTarget: PropTypes.object,
  body: PropTypes.object,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onOuterAction: PropTypes.func,
  popoverBodyStyles: PropTypes.string,
  popoverIsOpen: PropTypes.bool,
  popoverStyles: PropTypes.string,
  popoverTipShapeStyles: PropTypes.string,
  popoverTipStyles: PropTypes.string,
};

export { PopoverWrapper as Popover };
